import { useEffect } from 'react';

const SEO = ({ 
  title = "Cervello Eduverse - Next-Generation Education Ecosystem | School Coaching, Career Development & Entrepreneurship", 
  description = "Cervello Eduverse is a future-ready education ecosystem bringing academics, mentorship, and real-world skills together under one premium learning experience. Programs for school students, college students, and entrepreneurs in Hyderabad.",
  keywords = "education, learning, online courses, student development, skill building, career development, entrepreneurship, junior academy, elevare, business lab, school coaching, career guidance, startup development, Hyderabad education, best coaching center Hyderabad, skill development courses, online learning India, career counseling, exam preparation, programming courses, digital marketing, business skills",
  url = "https://cervelloeduverse.com",
  image = "https://cervelloeduverse.com/images/cervello-og-image.jpg",
  type = "website",
  author = "Cervello Eduverse",
  robots = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  viewport = "width=device-width, initial-scale=1.0, viewport-fit=cover, minimum-scale=1.0",
  locale = "en_IN",
  siteName = "Cervello Eduverse",
  twitterCard = "summary_large_image",
  twitterSite = "@cervelloeduverse",
  twitterCreator = "@cervelloeduverse",
  canonical = "https://cervelloeduverse.com",
  schemaData = null,
  publishedTime = null,
  modifiedTime = null,
  section = "Education",
  tags = [],
  // AEO (Answer Engine Optimization) specific props
  questionAnswer = null,
  faqData = null,
  howToData = null,
  // AIO (AI Optimization) specific props
  aiOptimized = true,
  aiSummary = null,
  keyTakeaways = []
}) => {
  useEffect(() => {
    // Clean up existing SEO meta tags to avoid duplication
    const existingMetas = document.querySelectorAll('[data-seo="cervello"]');
    existingMetas.forEach(meta => meta.remove());

    // Meta tags to be added - Enhanced for AEO, AIO, and SEO
    const metaTags = [
      // Standard meta tags
      { name: "title", content: title },
      { name: "description", content: description },
      { name: "keywords", content: keywords },
      { name: "author", content: author },
      { name: "robots", content: robots },
      { name: "viewport", content: viewport },
      { name: "theme-color", content: "#0B1C2D" },
      { name: "application-name", content: siteName },
      { name: "msapplication-TileColor", content: "#0B1C2D" },
      { name: "apple-mobile-web-app-title", content: siteName },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "mobile-web-app-status-bar-style", content: "black-translucent" },

      // Open Graph tags (Facebook, LinkedIn)
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: type },
      { property: "og:url", content: url },
      { property: "og:site_name", content: siteName },
      { property: "og:locale", content: locale },
      { property: "og:image", content: image },
      { property: "og:image:alt", content: title },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:secure_url", content: image },
      
      // Article tags (for blog posts and articles)
      ...(publishedTime ? [{ property: "article:published_time", content: publishedTime }] : []),
      ...(modifiedTime ? [{ property: "article:modified_time", content: modifiedTime }] : []),
      ...(section ? [{ property: "article:section", content: section }] : []),
      ...tags.map(tag => ({ property: "article:tag", content: tag })),

      // Twitter Card tags
      { name: "twitter:card", content: twitterCard },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: image },
      { name: "twitter:site", content: twitterSite },
      { name: "twitter:creator", content: twitterCreator },
      { name: "twitter:image:alt", content: title },
      
      // Google-specific tags
      { name: "googlebot", content: robots },
      { name: "format-detection", content: "telephone=yes" },
      
      // AEO (Answer Engine Optimization) Tags
      { name: "answer-engine-optimization", content: "enabled" },
      { name: "question-answer-format", content: "structured" },
      { name: "featured-snippet-target", content: "optimized" },
      
      // AIO (AI Optimization) Tags
      ...(aiOptimized ? [{ name: "ai-crawler-friendly", content: "yes" }] : []),
      { name: "machine-readable-summary", content: aiSummary || description },
      { name: "content-clarity-score", content: "high" },
    ];

    // Add meta tags to head
    metaTags.forEach(tagInfo => {
      const meta = document.createElement("meta");
      if (tagInfo.property) {
        meta.setAttribute("property", tagInfo.property);
      } else {
        meta.setAttribute("name", tagInfo.name);
      }
      meta.setAttribute("content", tagInfo.content);
      meta.setAttribute("data-seo", "cervello");
      document.head.appendChild(meta);
    });

    // Add canonical URL
    const canonicalLink = document.createElement("link");
    canonicalLink.rel = "canonical";
    canonicalLink.href = canonical;
    canonicalLink.setAttribute("data-seo", "cervello");
    document.head.appendChild(canonicalLink);

    // Add alternate language links (if needed)
    const alternateLinks = [
      { rel: "alternate", hrefLang: "x-default", href: url },
      { rel: "alternate", hrefLang: "en-in", href: url },
      { rel: "alternate", hrefLang: "hi", href: `${url}/hi` }, // Future Hindi version
    ];
    
    alternateLinks.forEach(linkData => {
      const link = document.createElement("link");
      link.rel = linkData.rel;
      link.hrefLang = linkData.hrefLang;
      link.href = linkData.href;
      link.setAttribute("data-seo", "cervello");
      document.head.appendChild(link);
    });

    // Add preconnect hints for performance
    const preconnectLinks = [
      "https://fonts.googleapis.com",
      "https://fonts.gstatic.com",
      "https://www.google-analytics.com",
    ];
    
    preconnectLinks.forEach(href => {
      const link = document.createElement("link");
      link.rel = "preconnect";
      link.href = href;
      link.crossOrigin = "anonymous";
      link.setAttribute("data-seo", "cervello");
      document.head.appendChild(link);
    });

    // Add DNS prefetch for external resources
    const dnsPrefetchLinks = [
      "https://www.googletagmanager.com",
      "https://www.google.com",
    ];
    
    dnsPrefetchLinks.forEach(href => {
      const link = document.createElement("link");
      link.rel = "dns-prefetch";
      link.href = href;
      link.setAttribute("data-seo", "cervello");
      document.head.appendChild(link);
    });

    // Add structured data (JSON-LD) if provided
    if (schemaData) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(schemaData);
      script.setAttribute("data-seo", "cervello");
      document.head.appendChild(script);
    }

    // Add QAPage schema for AEO (Question-Answer optimization)
    if (questionAnswer) {
      const qaSchema = {
        "@context": "https://schema.org",
        "@type": "QAPage",
        "mainEntity": {
          "@type": "Question",
          "name": questionAnswer.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": questionAnswer.answer
          }
        }
      };
      
      const qaScript = document.createElement("script");
      qaScript.type = "application/ld+json";
      qaScript.textContent = JSON.stringify(qaSchema);
      qaScript.setAttribute("data-seo", "cervello");
      document.head.appendChild(qaScript);
    }

    // Add FAQPage schema for AEO
    if (faqData && faqData.length > 0) {
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      };
      
      const faqScript = document.createElement("script");
      faqScript.type = "application/ld+json";
      faqScript.textContent = JSON.stringify(faqSchema);
      faqScript.setAttribute("data-seo", "cervello");
      document.head.appendChild(faqScript);
    }

    // Add HowTo schema for AEO
    if (howToData) {
      const howToSchema = {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": howToData.name,
        "description": howToData.description,
        "step": howToData.steps.map(step => ({
          "@type": "HowToStep",
          "text": step
        }))
      };
      
      const howToScript = document.createElement("script");
      howToScript.type = "application/ld+json";
      howToScript.textContent = JSON.stringify(howToSchema);
      howToScript.setAttribute("data-seo", "cervello");
      document.head.appendChild(howToScript);
    }

    // Update document title
    document.title = title;

    // Cleanup function to remove meta tags when component unmounts
    return () => {
      const seoElements = document.querySelectorAll('[data-seo="cervello"]');
      seoElements.forEach(el => el.remove());
    };
  }, [
    title, 
    description, 
    keywords, 
    url, 
    image, 
    type, 
    author, 
    robots, 
    viewport, 
    locale, 
    siteName, 
    twitterCard, 
    twitterSite, 
    twitterCreator, 
    canonical, 
    schemaData,
    publishedTime,
    modifiedTime,
    section,
    tags,
    questionAnswer,
    faqData,
    howToData,
    aiOptimized,
    aiSummary,
    keyTakeaways
  ]);

  return null;
};

export default SEO;